// Overflow handling for the jgd-variant-picker block:
// - the "+N" button reveals all values of an option, "show less" collapses again
// - in the collapsed row the checked value is always kept visible (the
//   overflow-list 'reflow' event places it in the last visible position)
// - expanded state survives the section re-render triggered by a variant change
//
// Expanding sets the overflow-list's `disabled` attribute (its
// attributeChangedCallback then moves every item back into the default slot);
// removing the attribute makes the component re-measure and collapse.

const expandedLists = new Set();

/**
 * @param {Element} list - an <overflow-list> inside the jgd variant picker
 * @returns {string} a key that stays stable across section re-renders
 */
function listKey(list) {
  const picker = list.closest('variant-picker');
  const fieldset = list.closest('fieldset');
  return `${picker?.dataset.blockId}:${fieldset?.dataset.fieldsetIndex}`;
}

/**
 * @param {Element} list
 * @param {boolean} [moveFocus] - focus the first revealed input (after a click on "+N")
 */
function expand(list, moveFocus = false) {
  const firstHiddenInput = list.querySelector(':scope > li[slot="overflow"] input');

  list.setAttribute('disabled', 'true');
  expandedLists.add(listKey(list));

  // The placeholder reserved space for the "+N" button; without this it would
  // leave a gap at the end of the expanded rows.
  const placeholder = list.shadowRoot?.querySelector('[part="placeholder"]');
  if (placeholder) placeholder.hidden = true;

  if (moveFocus) firstHiddenInput?.focus();
}

/**
 * @param {Element} list
 * @param {boolean} [moveFocus] - focus the "+N" button so focus does not get lost
 */
function collapse(list, moveFocus = false) {
  expandedLists.delete(listKey(list));
  list.removeAttribute('disabled');
  keepCheckedVisible(list);

  if (moveFocus) {
    requestAnimationFrame(() => list.querySelector('.jgd-variant-show-all')?.focus());
  }
}

/** Ask the overflow-list to re-measure with the checked value pinned into view. */
function keepCheckedVisible(list) {
  const checkedItem = list.querySelector(':scope > li:has(input:checked)');
  if (!checkedItem) return;

  list.dispatchEvent(new CustomEvent('reflow', { detail: { lastVisibleElement: checkedItem } }));
}

/** Re-apply expansion / checked visibility (initially and after re-renders). */
function syncLists() {
  for (const list of document.querySelectorAll('.jgd-variant-overflow')) {
    if (expandedLists.has(listKey(list))) {
      expand(list);
    } else if (list.querySelector(':scope > li[slot="overflow"] input:checked')) {
      keepCheckedVisible(list);
    }
  }
}

document.addEventListener('click', (event) => {
  if (!(event.target instanceof Element)) return;

  const showAll = event.target.closest('.jgd-variant-show-all');
  const showLess = event.target.closest('.jgd-variant-show-less');
  const list = (showAll || showLess)?.closest('.jgd-variant-overflow');
  if (!list) return;

  event.preventDefault();
  if (showAll) expand(list, true);
  else collapse(list, true);
});

// Desktop: hovering a color tile previews its name in the option label
// ("Farbe: weiß" → hovered name, back to the selected one on leave).
if (window.matchMedia('(hover: hover)').matches) {
  const legendValue = (label) =>
    label.closest('fieldset')?.querySelector('legend .variant-option__swatch-value');

  document.addEventListener('pointerover', (event) => {
    if (!(event.target instanceof Element)) return;
    const label = event.target.closest('.jgd-variant-option--tiles label.variant-option__button-label');
    if (!label) return;

    const value = label.querySelector('input')?.value;
    const span = legendValue(label);
    if (span && value) span.textContent = value;
  });

  document.addEventListener('pointerout', (event) => {
    if (!(event.target instanceof Element)) return;
    const label = event.target.closest('.jgd-variant-option--tiles label.variant-option__button-label');
    if (!label) return;

    const span = legendValue(label);
    const checked = label.closest('fieldset')?.querySelector('input:checked');
    if (span && checked) span.textContent = checked.value;
  });
}

// No eager initial check — the overflow-list assigns slots only after its own
// measurement. Every reflow ends by writing --overflow-count into the style
// attribute; observing that runs our sync exactly when a measurement happened,
// regardless of which module loaded first. MutationObserver callbacks are
// microtasks, so re-pinning lands in the same frame — no visible flicker.
const observedLists = new WeakSet();

// The inline pre-measurement guard (see jgd-variant-main-picker.liquid) uses
// ::part rules, which would override even the component's inline measurement
// styles. Lift the guard once the shadow stylesheet is loaded and force a
// re-measure in case the component measured while the guard was active.
function liftPreMeasurementGuard(list) {
  if (list.hasAttribute('data-jgd-css-ready')) return;

  const mark = () => {
    list.setAttribute('data-jgd-css-ready', '');
    list.dispatchEvent(new CustomEvent('reflow', { detail: {} }));
  };
  const sheetLink = list.shadowRoot?.querySelector('link[rel="stylesheet"]');
  if (!sheetLink || sheetLink.sheet) mark();
  else sheetLink.addEventListener('load', mark, { once: true });
}

function watchLists() {
  for (const list of document.querySelectorAll('.jgd-variant-overflow')) {
    // Re-check every run: a section re-render may have stripped the attribute.
    liftPreMeasurementGuard(list);

    if (observedLists.has(list)) continue;
    observedLists.add(list);
    new MutationObserver(syncLists).observe(list, { attributes: true, attributeFilter: ['style'] });
  }
  // Covers lists that already measured before this module was evaluated.
  syncLists();
}

watchLists();
// After a variant change the section is re-rendered; restore remembered
// expansion and observe any newly created lists.
document.addEventListener('variant:update', () => requestAnimationFrame(watchLists));

// --- Quick-add modal support ------------------------------------------------
//
// Horizon's quick-add modal fetches the product page and clones its grid into
// #quick-add-modal-content — long after this module initialized, so the cloned
// overflow-lists are never picked up by the init above. Worse, the clone lands
// while the dialog is still hidden: any measurement taken there reads width 0.
// Therefore (a) adopt lists as soon as content arrives, and (b) once the
// dialog is actually open (real layout), force a re-measure.
const quickAddModalContent = document.getElementById('quick-add-modal-content');
if (quickAddModalContent) {
  new MutationObserver(() => requestAnimationFrame(watchLists)).observe(quickAddModalContent, { childList: true });

  const quickAddDialog = quickAddModalContent.closest('dialog');
  if (quickAddDialog) {
    const remeasure = () => {
      if (!quickAddDialog.open) return;
      requestAnimationFrame(() => {
        watchLists();
        for (const list of quickAddDialog.querySelectorAll('.jgd-variant-overflow')) {
          list.dispatchEvent(new CustomEvent('reflow', { detail: {} }));
        }
      });
    };
    new MutationObserver(remeasure).observe(quickAddDialog, { attributes: true, attributeFilter: ['open'] });
    // On non-product pages this module first loads via the cloned modal
    // content itself — by then the dialog may already be open.
    remeasure();
  }
}

// --- Auto-switch to the nearest available combination (block setting) ------
//
// Active only when the snippet rendered the availability matrix
// (block setting "auto_available"). On every option click we check — in the
// CAPTURE phase, i.e. before the stock <variant-picker> change handler runs —
// whether the resulting combination is purchasable. If not, the other
// option(s) are re-pointed to the nearest available value via the stock
// component's public updateSelectedOption() API. The stock handler then reads
// the already corrected selection from the DOM: one single section render,
// no visible "sold out" flicker.
//
// Matrix format per variant: [valueIndex per option ..., available(1/0)];
// indices match the radios' data-input-index.

/** True while a corrected selection waits for its variant:update (URL fix). */
let pendingUrlFix = false;

document.addEventListener(
  'change',
  (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.type !== 'radio') return;

    const picker = input.closest('variant-picker.jgd-variant-picker');
    if (!picker) return;
    // Combined listing values load another product — never redirect those.
    if (input.dataset.connectedProductUrl) return;
    // Dropdown-rendered options have no radios; selection state would be
    // incomplete. The feature targets the button/tile style.
    if (picker.querySelector('select.variant-option__select')) return;

    const matrixScript = picker.querySelector('script[data-jgd-variant-matrix]');
    if (!matrixScript) return;

    /** @type {number[][]} */
    let matrix;
    try {
      matrix = JSON.parse(matrixScript.textContent ?? '');
    } catch {
      return;
    }
    if (!Array.isArray(matrix) || !matrix.length) return;

    const optionCount = matrix[0].length - 1;
    const changedPos = Number(input.dataset.fieldsetIndex);
    const clickedIndex = Number(input.dataset.inputIndex);
    if (Number.isNaN(changedPos) || Number.isNaN(clickedIndex)) return;

    // Current selection as value index per option. Options with a single
    // value render as info text (no fieldset) → index 0.
    const current = [];
    for (let pos = 0; pos < optionCount; pos++) {
      const checked = picker.querySelector(`fieldset[data-fieldset-index="${pos}"] input:checked`);
      current[pos] = checked ? Number(checked.dataset.inputIndex) : 0;
    }
    current[changedPos] = clickedIndex;

    const isAvailable = (row) => row[optionCount] === 1;
    const matchesCurrent = (row) => current.every((valueIndex, pos) => row[pos] === valueIndex);

    // Combination exists and is purchasable → nothing to do.
    if (matrix.some((row) => matchesCurrent(row) && isAvailable(row))) return;

    // All purchasable variants that keep the just-clicked value. None at all
    // → keep stock behavior (strikethrough + "sold out").
    const candidates = matrix.filter((row) => row[changedPos] === clickedIndex && isAvailable(row));
    if (!candidates.length) return;

    // Nearest by index distance across the other options; ties resolve
    // upwards (larger value index wins).
    const distance = (row) => {
      let sum = 0;
      for (let pos = 0; pos < optionCount; pos++) {
        if (pos !== changedPos) sum += Math.abs(row[pos] - current[pos]);
      }
      return sum;
    };
    const indexSum = (row) => {
      let sum = 0;
      for (let pos = 0; pos < optionCount; pos++) {
        if (pos !== changedPos) sum += row[pos];
      }
      return sum;
    };
    const target = [...candidates].sort((a, b) => distance(a) - distance(b) || indexSum(b) - indexSum(a))[0];

    for (let pos = 0; pos < optionCount; pos++) {
      if (pos === changedPos || target[pos] === current[pos]) continue;
      const radio = picker.querySelector(
        `fieldset[data-fieldset-index="${pos}"] input[data-input-index="${target[pos]}"]`
      );
      if (!radio) continue;
      // Public stock API: sets checked state and keeps the component's
      // internal bookkeeping (pill animation widths) in sync.
      if (typeof picker.updateSelectedOption === 'function') picker.updateSelectedOption(radio);
      else radio.checked = true;
    }

    // The stock handler writes the ?variant= URL param from the clicked
    // radio's data-variant-id, which for an unavailable combination is stale
    // or missing — remember to fix the URL once the render confirms the
    // resolved variant.
    const isOnProductPage =
      picker.dataset.templateProductMatch === 'true' &&
      !input.closest('product-card') &&
      !input.closest('quick-add-dialog');
    if (isOnProductPage) pendingUrlFix = true;
  },
  { capture: true }
);

document.addEventListener('variant:update', (event) => {
  if (!pendingUrlFix) return;
  pendingUrlFix = false;

  const variantId = event.detail?.resource?.id;
  if (!variantId) return;

  const url = new URL(window.location.href);
  url.searchParams.set('variant', String(variantId));
  if (url.href !== window.location.href) history.replaceState({}, '', url.toString());
});
